import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Clock, CheckCircle2, MapPin, BadgeCheck, AlertTriangle, X, FileText, Download, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const SectionGrid = ({ id, testid, data, muted = false }) => {
  const [selected, setSelected] = useState(null);

  return (
    <section
      id={id}
      data-testid={testid}
      className={`py-24 md:py-32 ${muted ? "bg-[#F4F2F7]" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-5">
            {data.overline}
          </p>
          <h2 className="font-serif-display text-3xl md:text-5xl font-semibold text-[#2D1B4E] tracking-tight leading-[1.1]">
            {data.title}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.items.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`${testid}-card-${i}`}
                className={`${
                  muted ? "bg-white" : "bg-[#F4F2F7]"
                } border border-[#E8E4EE] rounded-sm p-7 flex flex-col hover:-translate-y-1 transition-transform duration-300`}
              >
                <span className="flex items-center justify-center w-12 h-12 bg-white border border-[#E8E4EE] rounded-sm">
                  <Icon strokeWidth={1.5} className="w-6 h-6 text-[#5B3E96]" />
                </span>
                <h3 className="font-serif-display text-xl font-semibold text-[#2D1B4E] mt-4">
                  {item.title}
                </h3>
                <p className="text-sm text-[#5F5670] mt-2 leading-relaxed">{item.desc}</p>
                {item.notice && (
                  <div
                    data-testid={`${testid}-notice-${i}`}
                    className="mt-4 flex items-start gap-2 bg-[#FBF7E9] border border-[#EADFAE] rounded-sm px-3 py-2.5"
                  >
                    <AlertTriangle className="w-4 h-4 text-[#B8860B] shrink-0 mt-0.5 animate-pulse" />
                    <p className="text-sm font-semibold text-[#7A5B12] leading-snug uppercase tracking-wide">
                      {item.notice}
                    </p>
                  </div>
                )}
                {item.details && (
                  <button
                    type="button"
                    onClick={() => setSelected(item)}
                    data-testid={`${testid}-vermas-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 bg-[#2D1B4E] text-white hover:bg-[#1A1523] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ver más
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid={`${testid}-modal`}>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] leading-tight text-left">
              {selected?.title}
            </DialogTitle>
          </DialogHeader>
          {selected?.details && (
            <div className="mt-1">
              {selected.details.tagline && (
                <p className="font-serif-display text-xl text-[#5B3E96]">
                  {selected.details.tagline}
                </p>
              )}
              {selected.details.intro && (
                <p className="text-sm text-[#5F5670] mt-2 leading-relaxed">
                  {selected.details.intro}
                </p>
              )}
              {selected.details.image && (
                <a
                  href={selected.details.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${testid}-modal-image`}
                  className="mt-4 block border border-[#E8E4EE] rounded-sm overflow-hidden group"
                >
                  <img
                    src={selected.details.image}
                    alt={selected.title}
                    className="w-full group-hover:opacity-95 transition-opacity"
                    loading="lazy"
                  />
                </a>
              )}
              {selected.details.contact && (
                <div className="mt-4 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                  <p className="text-sm text-[#1A1523]">
                    <span className="font-semibold">Profesor(a):</span> {selected.details.contact.prof}
                  </p>
                  <a
                    href={`mailto:${selected.details.contact.email}`}
                    className="text-sm font-medium text-[#5B3E96] underline decoration-2 underline-offset-2 hover:text-[#2D1B4E] break-all"
                  >
                    {selected.details.contact.email}
                  </a>
                </div>
              )}
              {Array.isArray(selected.details.schedule) && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
                    Horarios
                  </p>
                  <div className="space-y-3">
                    {selected.details.schedule.map((g, gi) => (
                      <div key={gi} className="bg-white border border-[#E8E4EE] rounded-sm p-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-serif-display text-lg font-semibold text-[#2D1B4E]">{g.day}</p>
                          {g.location && (
                            <span className="inline-flex items-center gap-1.5 bg-[#EDE8F5] text-[#5B3E96] text-xs font-medium rounded-full px-3 py-1">
                              <MapPin className="w-3.5 h-3.5" /> {g.location}
                            </span>
                          )}
                        </div>
                        <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                          {g.times.map((t, ti) => (
                            <li key={ti} className="flex items-center gap-2 text-sm text-[#5F5670]">
                              <Clock className="w-3.5 h-3.5 text-[#5B3E96] shrink-0" /> {t}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.details.services && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
                    {selected.details.servicesTitle || "Servicios"}
                  </p>
                  <ul className="space-y-3">
                    {selected.details.services.map((s, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                        <span className="text-sm text-[#1A1523] leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.details.benefits && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
                    {selected.details.benefitsTitle || "Beneficios"}
                  </p>
                  <ul className="space-y-3">
                    {selected.details.benefits.map((s, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                        <span className="text-sm text-[#1A1523] leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.details.location && (
                <div className="mt-4 flex items-center gap-3 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                  <MapPin strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-overline text-[#5F5670]">Dónde encontrarlo</p>
                    <p className="text-sm text-[#1A1523]">{selected.details.location}</p>
                  </div>
                </div>
              )}
              {selected.details.requirement && (
                <div className="mt-4 flex items-center gap-3 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                  <BadgeCheck strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-overline text-[#5F5670]">Requisito</p>
                    <p className="text-sm text-[#1A1523]">{selected.details.requirement}</p>
                  </div>
                </div>
              )}
              {typeof selected.details.schedule === "string" && (
                <div className="mt-4 flex items-center gap-3 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                  <Clock strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-overline text-[#5F5670]">Horario</p>
                    <p className="text-sm text-[#1A1523]">{selected.details.schedule}</p>
                  </div>
                </div>
              )}
              {selected.details.citaSteps && (
                <div className="mt-6 pt-6 border-t border-[#E8E4EE]">
                  <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">
                    {selected.details.citaTitle || "¿Cómo sacas cita?"}
                  </p>
                  <ol className="space-y-3">
                    {selected.details.citaSteps.map((s, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2D1B4E] text-white text-xs font-semibold shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-sm text-[#1A1523] leading-relaxed mt-0.5">{s}</span>
                      </li>
                    ))}
                  </ol>
                  {selected.details.citaNote && (
                    <p className="text-xs text-[#5F5670] italic mt-4">{selected.details.citaNote}</p>
                  )}
                </div>
              )}

              {selected.details.blocks?.map((b, bi) => (
                <div key={bi} className="mt-6">
                  {b.type === "text" && (
                    <p className="text-sm text-[#5F5670] leading-relaxed">{b.text}</p>
                  )}
                  {b.type === "modalities" && (
                    <div className="space-y-3">
                      {b.items.map((m, i) => (
                        <div key={i} className="bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm p-4">
                          <p className="text-sm font-semibold text-[#2D1B4E]">{m.title}</p>
                          <p className="text-sm text-[#5F5670] mt-1 leading-relaxed">{m.desc}</p>
                          {m.example && (
                            <p className="text-sm text-[#5F5670] mt-2 leading-relaxed italic">{m.example}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {b.type === "location" && (
                    <div className="flex items-start gap-3 bg-[#EDE8F5] border border-[#D8CCEC] rounded-sm px-4 py-3">
                      <MapPin strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] uppercase tracking-overline text-[#5B3E96] font-semibold">{b.label}</p>
                        <p className="text-sm text-[#1A1523] mt-0.5 leading-relaxed">{b.value}</p>
                      </div>
                    </div>
                  )}
                  {b.type === "steps" && (
                    <>
                      {b.title && (
                        <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">{b.title}</p>
                      )}
                      <ol className="space-y-3">
                        {b.items.map((s, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2D1B4E] text-white text-xs font-semibold shrink-0">
                              {i + 1}
                            </span>
                            <span className="text-sm text-[#1A1523] leading-relaxed mt-0.5">{s}</span>
                          </li>
                        ))}
                      </ol>
                    </>
                  )}
                  {b.type === "list" && b.variant === "warn" && (
                    <div className="bg-[#FBECEC] border border-[#E7C9C9] rounded-sm p-4">
                      {b.title && (
                        <p className="text-sm font-semibold text-[#A23B3B] mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> {b.title}
                        </p>
                      )}
                      <ul className="space-y-2">
                        {b.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#7A3B3B] leading-relaxed">
                            <X className="w-4 h-4 mt-0.5 shrink-0" /> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {b.type === "list" && b.variant !== "warn" && (
                    <>
                      {b.title && (
                        <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">{b.title}</p>
                      )}
                      <ul className="space-y-2.5">
                        {b.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-[#1A1523] leading-relaxed">
                            {b.variant === "check" ? (
                              <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                            ) : (
                              <span className="text-[#5B3E96] mt-1">•</span>
                            )}
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {b.type === "info" && (
                    <div className="flex items-start gap-3 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-overline text-[#5F5670]">{b.label}</p>
                        <p className="text-sm text-[#1A1523] mt-0.5 leading-relaxed">{b.value}</p>
                      </div>
                    </div>
                  )}
                  {b.type === "documents" && (
                    <div className="pt-2">
                      {b.title && (
                        <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">{b.title}</p>
                      )}
                      <div className="space-y-3">
                        {b.items.map((d, i) => (
                          <a
                            key={i}
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between gap-3 bg-[#F4F2F7] hover:bg-[#EDE8F5] border border-[#E8E4EE] rounded-sm px-4 py-3 transition-colors group"
                          >
                            <span className="flex items-center gap-3 text-sm text-[#1A1523]">
                              <FileText className="w-4 h-4 text-[#5B3E96] shrink-0" />
                              {d.label}
                            </span>
                            <Download className="w-4 h-4 text-[#5B3E96] group-hover:translate-y-0.5 transition-transform shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {b.type === "contacts" && (
                    <div className="pt-2">
                      {b.title && (
                        <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">{b.title}</p>
                      )}
                      <div className="space-y-3">
                        {b.items.map((c, i) => (
                          <div key={i} className="bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                            <p className="text-sm font-semibold text-[#2D1B4E]">{c.name}</p>
                            <a
                              href={`mailto:${c.email}`}
                              className="text-sm font-medium text-[#5B3E96] underline decoration-2 underline-offset-2 hover:text-[#2D1B4E] break-all"
                            >
                              {c.email}
                            </a>
                            {c.ext && (
                              <p className="text-xs text-[#5F5670] mt-1 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-[#5B3E96]" /> {c.ext}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
